import { supabase } from '../supabase/client';
import { eigentuemerAnzeigename } from '../eigentuemer/anzeige';
import { ladeEigentuemerIds } from './ticketEigentuemer';

// Ansprechpartner zum Übernehmen im Auftrag-/Angebots-Dialog: die Mieter des Objekts, seine
// Eigentümer (bei WEG die der Einheiten), die Dienstleister des Objekts und — falls die Vorbelegung
// aus der Quell-Mail kam — der Meldende selbst. Dazu eine Suche über alle Kontakte (#525).
// Ohne diese Liste musste jede Telefonnummer von Hand abgetippt werden.

export interface MeldenderKandidat {
  id: string;
  name: string;
  rolle: string;
  telefon: string | null;
  email: string | null;
  zusatz?: string | null;
}

interface Vorbelegung {
  name: string | null;
  rolle: string;
  telefon: string | null;
  email: string | null;
}

const zusammen = (a: string | null | undefined, b: string | null | undefined) =>
  [a, b].map((s) => (s || '').trim()).filter(Boolean).join(' ');

/** Reine Zusammenstellung — getrennt vom Laden, damit die Regeln testbar bleiben. */
type MieterRoh = { id: string; vorname: string | null; nachname: string | null; firma_name?: string | null; telefon: string | null; mobil: string | null; email: string | null; einheit?: string | null };
type EigentuemerRoh = { id: string; anrede?: string | null; vorname: string | null; nachname: string | null; firma_name?: string | null; telefon: string | null; email: string | null };
type DienstleisterRoh = { id: string; name: string | null; ansprechpartner?: string | null; telefon: string | null; email: string | null };
type KontaktRoh = { id: string; vorname: string | null; nachname: string | null; firma_name?: string | null; festnetz?: string | null; handynummer?: string | null; email: string | null; kontaktart?: string | null };

export const dienstleisterKandidat = (d: DienstleisterRoh): MeldenderKandidat => ({
  id: `dienstleister-${d.id}`, name: (d.name || '').trim(), rolle: 'Dienstleister', telefon: d.telefon || null, email: d.email || null,
  zusatz: (d.ansprechpartner || '').trim() || null,
});

export const kontaktKandidat = (k: KontaktRoh): MeldenderKandidat => ({
  id: `kontakt-${k.id}`, name: (k.firma_name || '').trim() || zusammen(k.vorname, k.nachname), rolle: 'Sonstige',
  telefon: k.handynummer || k.festnetz || null, email: k.email || null, zusatz: (k.kontaktart || '').trim() || 'Kontakt',
});

export function baueKandidaten(
  mieter: MieterRoh[],
  eigentuemer: EigentuemerRoh[],
  vorbelegung?: Vorbelegung | null,
  dienstleister: DienstleisterRoh[] = [],
): MeldenderKandidat[] {
  const liste: MeldenderKandidat[] = [];
  const gesehen = new Set<string>();
  const merke = (k: MeldenderKandidat) => {
    const schluessel = `${k.name.toLowerCase()}|${(k.email || '').toLowerCase()}`;
    if (!k.name || gesehen.has(schluessel)) return;
    gesehen.add(schluessel);
    liste.push(k);
  };

  // Der bereits vorbelegte Meldende zuerst — er stammt aus der Quell-Mail und steht sonst
  // in keiner Stammdaten-Tabelle.
  if (vorbelegung?.name) {
    merke({
      id: 'vorbelegt',
      name: vorbelegung.name,
      rolle: vorbelegung.rolle || '',
      telefon: vorbelegung.telefon,
      email: vorbelegung.email,
      zusatz: 'aus der Meldung',
    });
  }

  for (const m of mieter) {
    merke({
      id: `mieter-${m.id}`,
      name: (m.firma_name || '').trim() || zusammen(m.vorname, m.nachname),
      rolle: 'Mieter',
      telefon: m.telefon || m.mobil || null,
      email: m.email || null,
      zusatz: m.einheit || null,
    });
  }

  for (const e of eigentuemer) {
    const name = eigentuemerAnzeigename(e as any);
    merke({
      id: `eigentuemer-${e.id}`,
      name: name && name !== '—' ? name : '',
      rolle: 'Eigentümer',
      telefon: e.telefon || null,
      email: e.email || null,
    });
  }

  for (const d of dienstleister) merke(dienstleisterKandidat(d));

  return liste;
}

export async function ladeMeldenderKandidaten(
  objektId: string | null,
  vorbelegung?: Vorbelegung | null,
  einheitId?: string | null,
): Promise<MeldenderKandidat[]> {
  if (!objektId) return baueKandidaten([], [], vorbelegung);

  // Eigentümer: Objekt, bei WEG die laufenden Eigentümer der Einheiten (wie im Ticket, #513)
  const [mieterRes, eigIds, dlObjRes] = await Promise.all([
    supabase.from('mieter')
      .select('id, vorname, nachname, firma_name, telefon, mobil, email, einheit')
      .eq('objekt_id', objektId).order('nachname'),
    ladeEigentuemerIds(objektId, einheitId),
    supabase.from('dienstleister_objekt').select('dienstleister_id').eq('objekt_id', objektId).neq('aktiv', false),
  ]);

  const dlIds = [...new Set((dlObjRes.data || []).map((x: { dienstleister_id: string }) => x.dienstleister_id).filter(Boolean))];
  const [eigRes, dlRes] = await Promise.all([
    eigIds.length
      ? supabase.from('eigentuemer').select('id, anrede, vorname, nachname, firma_name, telefon, email').in('id', eigIds.slice(0, 100))
      : Promise.resolve({ data: [] }),
    dlIds.length
      ? supabase.from('dienstleister').select('id, name, ansprechpartner, telefon, email').in('id', dlIds).neq('aktiv', false).order('name')
      : Promise.resolve({ data: [] }),
  ]);

  return baueKandidaten((mieterRes.data || []) as any, (eigRes.data || []) as any, vorbelegung, (dlRes.data || []) as any);
}

/** #525: beliebigen Kontakt suchen – Mieter, Eigentümer, Dienstleister und Kontakte (Name/Firma). */
export async function sucheKontakte(text: string, proQuelle = 6): Promise<MeldenderKandidat[]> {
  const q = text.trim().replace(/[%_,()]/g, ' ').trim();
  if (q.length < 2) return [];
  const like = `%${q}%`;
  const [m, e, d, k] = await Promise.all([
    supabase.from('mieter').select('id, vorname, nachname, firma_name, telefon, mobil, email, einheit')
      .or(`vorname.ilike.${like},nachname.ilike.${like},firma_name.ilike.${like}`).limit(proQuelle),
    supabase.from('eigentuemer').select('id, anrede, vorname, nachname, firma_name, telefon, email')
      .or(`vorname.ilike.${like},nachname.ilike.${like},firma_name.ilike.${like}`).limit(proQuelle),
    supabase.from('dienstleister').select('id, name, ansprechpartner, telefon, email')
      .or(`name.ilike.${like},ansprechpartner.ilike.${like}`).limit(proQuelle),
    supabase.from('kontakte').select('id, vorname, nachname, firma_name, festnetz, handynummer, email, kontaktart')
      .or(`vorname.ilike.${like},nachname.ilike.${like},firma_name.ilike.${like}`).limit(proQuelle),
  ]);
  return [
    ...baueKandidaten((m.data || []) as any, (e.data || []) as any, null, (d.data || []) as any),
    ...((k.data || []) as KontaktRoh[]).map(kontaktKandidat).filter(x => x.name),
  ];
}
